// ProgressContext.tsx — the progress window for EVERY action, short or long: wrap the call in `track(...)` and the
// operator sees what is happening (the step, the time so far, then done or the reason it failed) instead of a
// greyed-out button. Short actions close themselves a moment after they succeed; a failure stays open until read.
//   const { track } = useProgress();
//   await track("Deleting the account", growthApi.deleteAccount(id), { done: () => "deleted" });
// Mounted once for the admin (AdminLayout). Outside a provider `track` is a pass-through (tests, fixtures).
import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";
import { ProgressModal } from "./ProgressModal.tsx";
import { startProgress, atStep, finish, fail, type Progress } from "./progress.ts";

export interface TrackOptions<T> {
  /** The steps shown (default: the action, then "Done"). The action runs as the first step. */
  steps?: string[];
  /** What is happening while it runs, in words. */
  detail?: string;
  /** The line shown when it succeeds. */
  done?: (result: T) => string;
  /** Keep the window open after success (default: it closes itself after a moment). */
  keepOpen?: boolean;
  /** Buttons shown when it's done. */
  links?: { label: string; href: string }[];
}
type Track = <T>(title: string, work: Promise<T>, o?: TrackOptions<T>) => Promise<T>;

const Ctx = createContext<Track | null>(null);

export function ProgressProvider({ children }: { children: ReactNode }) {
  const [p, setP] = useState<Progress | null>(null);
  const ref = useRef<Progress | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const show = (x: Progress | null) => { ref.current = x; setP(x); };
  const track = useCallback<Track>(async (title, work, o = {}) => {
    if (timer.current) clearTimeout(timer.current);
    const start = startProgress(title, o.steps ?? [title, "Done"]);
    show(atStep(start, 0, o.detail ?? null));
    try {
      const r = await work;
      const done = finish(ref.current ?? start, o.done ? o.done(r) : "done", o.links ?? []);
      show(done);
      if (!o.keepOpen) timer.current = setTimeout(() => { if (ref.current === done) show(null); }, 1400);
      return r;
    } catch (e) {
      show(fail(ref.current ?? start, e instanceof Error ? e.message : String(e)));
      throw e;   // the caller's own error handling still runs
    }
  }, []);
  return <Ctx.Provider value={track}>{children}<ProgressModal p={p} onClose={() => show(null)} /></Ctx.Provider>;
}

export function useProgress(): { track: Track } {
  const t = useContext(Ctx);
  return { track: t ?? (async (_title, work) => work) };
}
